import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Edit,
  Trash2,
  Plus,
  Users as UsersIcon,
  Search,
  Loader2,
  FolderOpen,
  Eye,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useGetTeamsListQuery,
  useDeleteTeamMutation,
  type TeamListItem,
} from '@/redux/service/teams'
import { useGetEmployeesQuery, type Employee } from '@/redux/service/employee'
import TeamFormDialog from './TeamFormDialog'
import TeamDetailDialog from './TeamDetailDialog'
import { initialsFor, avatarColor } from './teamUtils'
import { cn } from '@/lib/utils'

const Teams = () => {
  const { data, isLoading, isFetching, isError } = useGetTeamsListQuery()
  const { data: employeesData } = useGetEmployeesQuery({
    page: 1,
    limit: 500,
    salarySetup: 1,
  })
  const [deleteTeam, { isLoading: isDeleting }] = useDeleteTeamMutation()

  const [formOpen, setFormOpen] = useState(false)
  const [editTeam, setEditTeam] = useState<TeamListItem | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<TeamListItem | null>(null)
  const [detailTeamId, setDetailTeamId] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  const employeesByTeam = useMemo(() => {
    const map = new Map<number, Employee[]>()
    employeesData?.data?.forEach(e => {
      if (e.team_id == null) return
      const list = map.get(e.team_id) ?? []
      list.push(e)
      map.set(e.team_id, list)
    })
    return map
  }, [employeesData])

  const handleAdd = () => {
    setEditTeam(null)
    setFormOpen(true)
  }

  const handleEdit = (team: TeamListItem) => {
    setEditTeam(team)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteTeam(confirmDelete.id).unwrap()
      toast.success('Team deleted')
      setConfirmDelete(null)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to delete team')
    }
  }

  const allTeams = useMemo(() => data?.data ?? [], [data])
  const filteredTeams = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allTeams
    return allTeams.filter(t => t.name.toLowerCase().includes(q))
  }, [allTeams, search])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  return (
    <motion.div
      initial='hidden'
      animate='visible'
      variants={containerVariants}
      className='space-y-6'
    >
      <div className='flex items-center justify-between gap-4 flex-wrap pb-2 border-b border-border/50'>
        <div className='flex items-center gap-3'>
          <div className='h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm'>
            <UsersIcon className='w-5 h-5' />
          </div>
          <div>
            <h2 className='text-xl font-bold tracking-tight text-foreground'>
              Team Management
            </h2>
            <p className='text-xs text-muted-foreground font-medium'>
              {allTeams.length}{' '}
              {allTeams.length === 1 ? 'total team' : 'total teams'}
            </p>
          </div>
        </div>

        <div className='flex items-center gap-3 ml-auto'>
          <div className='relative min-w-[280px] group'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors' />
            <Input
              placeholder='Search teams...'
              value={search}
              onChange={e => setSearch(e.target.value)}
              className='pl-10 h-10 bg-card border-border/50 hover:bg-accent/30 focus-visible:ring-primary focus-visible:border-primary transition-all rounded-xl text-sm shadow-sm'
            />
          </div>
          <Button
            onClick={handleAdd}
            className='h-10 px-5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_8px_16px_-6px_rgba(var(--primary),0.3)] hover:shadow-[0_8px_20px_-4px_rgba(var(--primary),0.4)] transition-all duration-300 rounded-xl font-bold flex items-center gap-2 group'
          >
            <Plus className='w-4 h-4 group-hover:rotate-90 transition-transform' />
            Create Team
          </Button>
        </div>
      </div>

      <div className='relative'>
        <AnimatePresence mode='wait'>
          {isLoading || isFetching ? (
            <motion.div
              key='loading'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className=' overflow-hidden bg-card/50  !border-none'
            >
              <Table className='border-none'>
                <TableHeader>
                  <TableRow className='bg-muted/30 border-b border-border/50'>
                    <TableHead className='text-foreground font-bold h-12'>
                      Team
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Leader
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      PM
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Source Teams
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Skills
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Members
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12 text-right'>
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className='border-b border-border/40'>
                      <TableCell colSpan={6} className='py-4'>
                        <div className='h-8 bg-muted/50 animate-pulse rounded-lg' />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </motion.div>
          ) : isError ? (
            <motion.div
              key='error'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className='flex flex-col items-center justify-center text-muted-foreground gap-4 py-32 bg-card/50 rounded-2xl border border-border/50 border-dashed'
            >
              <div className='h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mb-2'>
                <UsersIcon className='h-8 w-8' />
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-foreground'>
                  Failed to load teams
                </p>
                <p className='text-sm text-muted-foreground max-w-xs mx-auto'>
                  An error occurred while fetching the team list. Please try
                  again.
                </p>
              </div>
              <Button
                variant='outline'
                onClick={() => window.location.reload()}
                className='mt-2 rounded-xl'
              >
                Retry Connection
              </Button>
            </motion.div>
          ) : filteredTeams.length === 0 ? (
            <motion.div
              key='empty'
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className='flex flex-col items-center justify-center text-muted-foreground gap-4 py-32 bg-card/50 rounded-2xl border border-border/50 border-dashed shadow-inner'
            >
              <div className='h-20 w-20 rounded-3xl bg-muted/50 flex items-center justify-center text-muted-foreground/30 mb-2 rotate-12'>
                <UsersIcon className='h-10 w-10' />
              </div>
              <div className='text-center space-y-1'>
                <p className='text-lg font-bold text-foreground'>
                  {search ? 'No matches found' : 'No teams created yet'}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {search
                    ? 'Try adjusting your search query'
                    : 'Start by creating your first workspace team'}
                </p>
              </div>
              {!search && (
                <Button
                  variant='secondary'
                  onClick={handleAdd}
                  className='mt-4 rounded-xl font-bold bg-primary/10 text-primary hover:bg-primary/20 transition-all border border-primary/20'
                >
                  <Plus className='w-4 h-4 mr-2' />
                  Add New Team
                </Button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key='content'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className='rounded-sm overflow-hidden bg-card shadow-sm'
            >
              <Table>
                <TableHeader>
                  <TableRow className='bg-muted/30 border-b border-border/50'>
                    <TableHead className='text-foreground font-bold h-12 pl-6'>
                      Team
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Leader
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      PM
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Source Teams
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Skills
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12'>
                      Members
                    </TableHead>
                    <TableHead className='text-foreground font-bold h-12 text-right pr-6'>
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeams.map(team => (
                    <TeamRow
                      key={team.id}
                      team={team}
                      members={employeesByTeam.get(team.id) ?? []}
                      onView={() => setDetailTeamId(team.id)}
                      onEdit={() => handleEdit(team)}
                      onDelete={() => setConfirmDelete(team)}
                    />
                  ))}
                </TableBody>
              </Table>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <TeamFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        team={editTeam}
      />

      <TeamDetailDialog
        teamId={detailTeamId}
        onClose={() => setDetailTeamId(null)}
      />

      <Dialog
        open={!!confirmDelete}
        onOpenChange={open => !open && setConfirmDelete(null)}
      >
        <DialogContent className='max-w-[440px] rounded-2xl p-0 overflow-hidden border-border bg-background shadow-2xl'>
          <div className='bg-destructive/5 p-8 pb-6 text-center border-b border-border/50'>
            <div className='h-16 w-16 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4'>
              <Trash2 className='h-8 w-8' />
            </div>
            <DialogTitle className='text-xl font-bold text-foreground mb-2'>
              Delete Team
            </DialogTitle>
            <DialogDescription className='text-muted-foreground'>
              Are you sure you want to delete{' '}
              <span className='font-bold text-foreground underline decoration-destructive/30'>
                {confirmDelete?.name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </div>
          <div className='p-6 bg-muted/10 flex gap-3'>
            <Button
              variant='ghost'
              className='flex-1 h-11 rounded-xl font-bold'
              onClick={() => setConfirmDelete(null)}
            >
              Keep Team
            </Button>
            <Button
              variant='destructive'
              disabled={isDeleting}
              onClick={handleDelete}
              className='flex-1 h-11 rounded-xl font-bold shadow-lg shadow-destructive/20'
            >
              {isDeleting ? (
                <div className='flex items-center gap-2'>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  <span>Deleting...</span>
                </div>
              ) : (
                'Delete Team'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

interface TeamRowProps {
  team: TeamListItem
  members: Employee[]
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}

const TeamRow = ({ team, members, onView, onEdit, onDelete }: TeamRowProps) => {
  const leaderName = team.leader_user_name || team.leader_employee_name

  const aggregatedSkills = useMemo(() => {
    const counts = new Map<string, number>()
    members.forEach(m => {
      m.skills?.forEach(s => {
        counts.set(s, (counts.get(s) ?? 0) + 1)
      })
    })
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [members])

  const folders = team.folders ?? []
  const totalMembers =
    (team.user_member_count ?? 0) + (team.employee_member_count ?? 0)
  const initials = initialsFor(team.name)
  const colorClass = avatarColor(team.name)
  const isInactive = team.is_active === false

  return (
    <TableRow
      className={cn(
        'border-b border-border/40 group hover:bg-accent/30 cursor-pointer transition-colors',
        isInactive && 'bg-muted/50'
      )}
    >
      <TableCell className='align-middle pl-6 truncate'>
        <div className='flex items-center gap-4 min-w-0'>
          <div
            className={cn(
              'h-11 w-11 rounded-2xl flex items-center justify-center text-xs font-bold shrink-0 shadow-sm border-2 border-white/10 group-hover:scale-105 transition-transform duration-300',
              colorClass
            )}
          >
            {initials}
          </div>
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='font-bold text-foreground text-sm tracking-tight truncate'>
                {team.name}
              </span>
              {isInactive && (
                <span className='text-[10px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded-md text-muted-foreground bg-muted border border-border shadow-xs'>
                  Inactive
                </span>
              )}
            </div>
            {folders.length > 0 && (
              <div className='flex items-center gap-1 mt-0.5'>
                <FolderOpen className='w-2.5 h-2.5 text-muted-foreground/70' />
                <span className='text-[10px] text-muted-foreground font-medium truncate max-w-[120px]'>
                  {folders[0]?.folder_name ?? 'No folder assigned'}{' '}
                  {folders.length > 1 && `+ ${folders.length - 1} more`}
                </span>
              </div>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell className='align-middle truncate'>
        {leaderName ? (
          <div className='inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-500/5 border border-amber-500/10 text-foreground group-hover:bg-amber-500/10 transition-colors'>
            <div className='flex flex-col min-w-0'>
              <span className='text-sm font-bold truncate leading-none'>
                {leaderName}
              </span>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-1.5 text-xs text-muted-foreground font-medium opacity-60'>
            <div className='w-1 h-1 rounded-full bg-muted-foreground/50' />
            No leader
          </div>
        )}
      </TableCell>

      <TableCell className='align-middle truncate'>
        {team.project_manager_name ? (
          <div className='inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-500/5 border border-blue-500/10 text-foreground group-hover:bg-blue-500/10 transition-colors'>
            <div className='flex flex-col min-w-0'>
              <span className='text-sm font-bold truncate leading-none'>
                {team.project_manager_name}
              </span>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-1.5 text-xs text-muted-foreground font-medium opacity-60'>
            <div className='w-1 h-1 rounded-full bg-muted-foreground/50' />
            No PM
          </div>
        )}
      </TableCell>

      <TableCell className='align-middle truncate'>
        {folders.length > 0 ? (
          <div className='flex flex-wrap gap-1.5  truncate'>
            {folders.slice(0, 2).map(f => (
              <span
                key={f.folder_id}
                className='text-[10px] font-bold uppercase tracking-tight px-2 py-0.5 rounded-lg text-primary/80 bg-primary/5 border border-primary/10 shadow-xs'
              >
                {f.folder_name}
              </span>
            ))}
            {folders.length > 2 && (
              <span className='text-[10px] font-bold uppercase tracking-tight px-2 py-0.5 rounded-lg text-muted-foreground bg-muted border border-border shadow-xs'>
                +{folders.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span className='text-xs text-muted-foreground font-medium opacity-50'>
            —
          </span>
        )}
      </TableCell>

      <TableCell className='align-middle truncate'>
        {aggregatedSkills.length > 0 ? (
          <div className='flex flex-wrap gap-1.5 truncate '>
            {aggregatedSkills.slice(0, 3).map(([skill]) => (
              <span
                key={skill}
                className='text-[11px] font-medium px-2.5 py-0.5 rounded-lg bg-card text-foreground border border-border group-hover:border-primary/20 group-hover:bg-primary/5 transition-all'
              >
                {skill}
              </span>
            ))}
            {aggregatedSkills.length > 3 && (
              <span className='text-[11px] font-medium px-2 py-0.5 rounded-lg bg-muted text-muted-foreground border border-border'>
                +{aggregatedSkills.length - 3}
              </span>
            )}
          </div>
        ) : (
          <span className='text-xs text-muted-foreground font-medium opacity-50'>
            —
          </span>
        )}
      </TableCell>

      <TableCell className='align-middle truncate'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-accent/30 border border-border/50 text-foreground group-hover:bg-accent/50 transition-colors'>
          <UsersIcon className='w-3.5 h-3.5 text-primary/60' />
          <span className='text-sm font-bold'>{totalMembers}</span>
        </div>
      </TableCell>

      <TableCell className='align-middle text-right pr-6 truncate'>
        <div className='flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0'>
          <Button
            variant='outline'
            size='sm'
            onClick={onView}
            className='h-9 px-3 rounded-xl border-border bg-background hover:bg-primary/5 hover:text-primary hover:border-primary/20 font-bold text-xs gap-1.5 shadow-sm'
          >
            <Eye className='w-4 h-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            onClick={onEdit}
            className='h-9 w-9 rounded-xl border-border bg-background hover:bg-primary/5 hover:text-primary hover:border-primary/20 shadow-sm'
          >
            <Edit className='w-4 h-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            onClick={onDelete}
            className='h-9 w-9 rounded-xl border-border bg-background hover:bg-destructive/5 hover:text-destructive hover:border-destructive/20 shadow-sm'
          >
            <Trash2 className='w-4 h-4' />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

export default Teams
